/**
 * CSV Data Loader for Spot Price Data
 * Supports Danish CSV format (semicolon separator, comma decimal)
 */

/**
 * Parse CSV spot price file
 * @param {string} csvText - Raw CSV text content
 * @returns {Array} Array of {hour, spotPrice, timestamp} objects
 */
export function parseSpotPriceCSV(csvText) {
  const lines = csvText.split('\n').filter(line => line.trim());
  const data = [];

  // Skip header row (starts at line 1)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Parse semicolon-separated values
    const parts = line.split(';');
    if (parts.length < 3) continue;

    const deliveryStart = parts[0].trim();
    const priceStr = parts[2].trim();

    // Convert Danish decimal format (comma) to JavaScript decimal (dot)
    const spotPrice = parseFloat(priceStr.replace(',', '.'));

    if (isNaN(spotPrice)) {
      console.warn(`Invalid price at line ${i}: ${priceStr}`);
      continue;
    }

    // Parse date (format: DD.MM.YYYY HH:MM)
    const timestamp = parseDanishDateTime(deliveryStart);

    data.push({
      hour: data.length, // Sequential hour index
      spotPrice,
      timestamp,
      dateString: deliveryStart
    });
  }

  console.log(`✅ Loaded ${data.length} hours of spot price data`);
  console.log(`📅 Range: ${data[0]?.dateString} to ${data[data.length - 1]?.dateString}`);
  console.log(`💰 Price range: ${Math.min(...data.map(d => d.spotPrice)).toFixed(2)} - ${Math.max(...data.map(d => d.spotPrice)).toFixed(2)} DKK/MWh`);

  return data;
}

/**
 * Parse Danish date format (DD.MM.YYYY HH:MM)
 * @param {string} dateStr - Date string in Danish format
 * @returns {Date} JavaScript Date object
 */
function parseDanishDateTime(dateStr) {
  // Format: "31.12.2023 23:00"
  const parts = dateStr.split(' ');
  if (parts.length !== 2) return null;

  const dateParts = parts[0].split('.');
  const timeParts = parts[1].split(':');

  if (dateParts.length !== 3 || timeParts.length !== 2) return null;

  const day = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1; // JavaScript months are 0-indexed
  const year = parseInt(dateParts[2], 10);
  const hour = parseInt(timeParts[0], 10);
  const minute = parseInt(timeParts[1], 10);

  return new Date(year, month, day, hour, minute);
}

/**
 * Create a data provider from CSV spot price data
 * Compatible with existing simulation data provider interface
 * @param {Array} spotPriceData - Array of spot price objects from parseSpotPriceCSV
 * @param {Object} options - Configuration options
 * @returns {Object} Data provider with getSpotPrice, getSolarMW, etc. methods
 */
export function createCSVDataProvider(spotPriceData, options = {}) {
  const {
    // Optional: Provide demand profile (otherwise use flat 5 MW)
    demandProfile = null,
    // Optional: Include solar (default: none)
    includeSolar = false,
    solarProfile = null,
    // Optional: Include waste heat (default: none)
    includeWasteHeat = false,
    wasteHeatProfile = null
  } = options;

  return {
    getSpotPrice: (hour) => {
      if (hour < 0 || hour >= spotPriceData.length) return 0;
      return spotPriceData[hour].spotPrice;
    },

    getSolarMW: (hour) => {
      if (!includeSolar || !solarProfile) return 0;
      if (hour < 0 || hour >= solarProfile.length) return 0;
      return solarProfile[hour];
    },

    getWasteHeatMW: (hour) => {
      if (!includeWasteHeat || !wasteHeatProfile) return 0;
      if (hour < 0 || hour >= wasteHeatProfile.length) return 0;
      return wasteHeatProfile[hour];
    },

    getDemandMW: (hour) => {
      if (demandProfile) {
        if (hour < 0 || hour >= demandProfile.length) return 5; // Default fallback
        return demandProfile[hour];
      }
      // Default: Flat 5 MW demand
      return 5;
    },

    // Metadata
    getMetadata: () => ({
      source: 'CSV',
      hours: spotPriceData.length,
      dateRange: spotPriceData.length > 0 ? {
        start: spotPriceData[0].dateString,
        end: spotPriceData[spotPriceData.length - 1].dateString
      } : null,
      priceStats: {
        min: Math.min(...spotPriceData.map(d => d.spotPrice)),
        max: Math.max(...spotPriceData.map(d => d.spotPrice)),
        avg: spotPriceData.reduce((sum, d) => sum + d.spotPrice, 0) / spotPriceData.length
      }
    })
  };
}

/**
 * Load CSV file from public folder
 * @param {string} filename - Filename in public folder
 * @returns {Promise<Array>} Parsed spot price data
 */
export async function loadCSVFromPublic(filename) {
  try {
    const response = await fetch(`/${filename}`);
    if (!response.ok) {
      throw new Error(`Failed to load ${filename}: ${response.statusText}`);
    }
    const csvText = await response.text();
    return parseSpotPriceCSV(csvText);
  } catch (error) {
    console.error('Error loading CSV:', error);
    throw error;
  }
}

/**
 * Create demand profile from monthly distribution
 * @param {Array} monthlyGWh - Array of 12 monthly demand values in GWh
 * @param {number} totalHours - Total simulation hours (default 8784)
 * @returns {Array} Hourly demand in MW
 */
export function createDemandProfile(monthlyGWh, totalHours = 8784) {
  const profile = [];
  const hoursPerMonth = [744, 672, 744, 720, 744, 720, 744, 744, 720, 744, 720, 744]; // Hours in each month

  for (let month = 0; month < 12; month++) {
    const monthDemandGWh = monthlyGWh[month];
    const monthHours = hoursPerMonth[month];
    const hourlyDemandMW = (monthDemandGWh * 1000) / monthHours; // Convert GWh to MW

    for (let h = 0; h < monthHours && profile.length < totalHours; h++) {
      profile.push(hourlyDemandMW);
    }
  }

  // Fill remaining hours if any
  while (profile.length < totalHours) {
    profile.push(5); // Default 5 MW
  }

  return profile;
}

export default {
  parseSpotPriceCSV,
  createCSVDataProvider,
  loadCSVFromPublic,
  createDemandProfile
};
